import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { HttpHelpService } from '../service/http-help.service';
import { ChecksReportService } from '../../services/checks-report.service';
import {
  CheckReportRow,
  CheckStatus,
  ChecksReportPeriod,
  ChecksReportSummary,
} from '../../models/checks-report.model';
import { getDefaultReportLanguage } from 'src/app/shared/services/report-language-preference';

interface ChartBarItem {
  label: string;
  amount: number;
  pct: number;
}

interface ChartStatusItem {
  status: CheckStatus;
  label: string;
  count: number;
  amount: number;
  pct: number;
  color: string;
}

@Component({
  selector: 'app-checks-report',
  templateUrl: './checks-report.component.html',
  styleUrls: ['./checks-report.component.scss'],
})
export class ChecksReportComponent implements OnInit, OnDestroy {
  curentLang = 'ar';
  companyId: number | null = null;
  companyData: { company_id: number; label: string }[] = [];

  period: ChecksReportPeriod = 'all_future';
  customFromDate: Date | null = null;
  customToDate: Date | null = null;
  dateRangeError = '';

  checkStatusFilter: CheckStatus | '' = '';
  includeCancelled = false;
  searchKeyword = '';

  sort: 'ASC' | 'DESC' = 'ASC';
  sortIndex = 0;

  limit = 20;
  offset = 0;
  total = 0;
  page = 1;

  rows: CheckReportRow[] = [];
  summary: ChecksReportSummary | null = null;
  loading = false;
  updatingCheckId: number | null = null;

  chartBars: ChartBarItem[] = [];
  chartStatuses: ChartStatusItem[] = [];

  readonly statusOptions: { value: CheckStatus; labelKey: string }[] = [
    { value: 'pending', labelKey: 'pending' },
    { value: 'collected', labelKey: 'collected' },
    { value: 'returned', labelKey: 'returned' },
    { value: 'cancelled', labelKey: 'cancelled' },
  ];

  get statusFilterOptions(): { value: CheckStatus | ''; label: string }[] {
    return [
      { value: '', label: this.translate.instant('checksReport.filters.allStatuses') },
      ...this.statusOptions.map((s) => ({
        value: s.value,
        label: this.translate.instant(`checksReport.status.${s.labelKey}`),
      })),
    ];
  }

  readonly sortColumnIndexes: Record<string, number> = {
    due_date: 0,
    check_number: 1,
    amount: 2,
    customer_name: 3,
    days_until_due: 4,
  };

  private destroy$ = new Subject<void>();
  private onCompanyChangedHandler: EventListener | null = null;

  constructor(
    private checksReportService: ChecksReportService,
    private httpHelp: HttpHelpService,
    private spinner: NgxSpinnerService,
    public translate: TranslateService,
    private router: Router,
  ) {
    this.curentLang = localStorage.getItem('curentLang') || 'ar';
    this.loadCompanyId();
  }

  ngOnInit(): void {
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      this.curentLang = event.lang;
      this.loadReport();
    });

    this.curentLang = this.translate.currentLang || this.curentLang;

    this.loadCompanies();

    this.onCompanyChangedHandler = ((e: Event) => {
      const companyId = (e as CustomEvent<{ companyId?: number }>).detail?.companyId;
      if (companyId != null) {
        this.companyId = Number(companyId);
        this.offset = 0;
        this.loadReport();
      }
    }) as EventListener;
    window.addEventListener('companyChanged', this.onCompanyChangedHandler);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.onCompanyChangedHandler) {
      window.removeEventListener('companyChanged', this.onCompanyChangedHandler);
    }
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.limit));
  }

  get currentPage(): number {
    return Math.floor(this.offset / this.limit) + 1;
  }

  setPeriod(next: ChecksReportPeriod): void {
    this.period = next;
    this.offset = 0;
    this.loadReport();
  }

  onCustomDateChange(): void {
    this.dateRangeError = '';
    if (this.period !== 'custom') {
      return;
    }
    if (this.customFromDate && this.customToDate && this.customFromDate > this.customToDate) {
      this.dateRangeError = this.translate.instant('checksReport.errors.invalidDateRange');
    }
  }

  applySearch(): void {
    this.offset = 0;
    this.loadReport();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.applySearch();
    }
  }

  onStatusFilterChange(): void {
    this.offset = 0;
    this.loadReport();
  }

  onIncludeCancelledChange(): void {
    this.offset = 0;
    this.loadReport();
  }

  onCompanyChange(): void {
    this.offset = 0;
    this.loadReport();
  }

  onLimitChange(): void {
    this.offset = 0;
    this.loadReport();
  }

  onSortColumn(columnKey: string): void {
    const idx = this.sortColumnIndexes[columnKey];
    if (idx === undefined) {
      return;
    }
    if (this.sortIndex === idx) {
      this.sort = this.sort === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortIndex = idx;
      this.sort = 'ASC';
    }
    this.offset = 0;
    this.loadReport();
  }

  getSortClass(columnKey: string): string {
    const idx = this.sortColumnIndexes[columnKey];
    if (idx !== this.sortIndex) {
      return 'th-sortable';
    }
    return `th-sortable active ${this.sort === 'ASC' ? 'asc' : 'desc'}`;
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.offset = (page - 1) * this.limit;
    this.loadReport();
  }

  getPageNumbers(): (number | string)[] {
    const total = this.totalPages;
    const current = this.currentPage;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages: (number | string)[] = [1];
    if (current > 3) {
      pages.push('...');
    }
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let p = start; p <= end; p++) {
      pages.push(p);
    }
    if (current < total - 2) {
      pages.push('...');
    }
    pages.push(total);
    return pages;
  }

  loadReport(): void {
    if (!this.companyId) {
      return;
    }
    if (this.period === 'custom') {
      if (!this.customFromDate || !this.customToDate) {
        return;
      }
      if (this.customFromDate > this.customToDate) {
        this.dateRangeError = this.translate.instant('checksReport.errors.invalidDateRange');
        return;
      }
    }

    this.loading = true;
    this.spinner.show();

    const params = {
      period: this.period,
      companyId: this.companyId,
      fromDate: this.period === 'custom' ? this.toApiDate(this.customFromDate!) : undefined,
      toDate: this.period === 'custom' ? this.toApiDate(this.customToDate!) : undefined,
      check_status: this.checkStatusFilter || undefined,
      include_cancelled: this.includeCancelled,
      searchKeyword: this.searchKeyword.trim() || undefined,
      sort: this.sort,
      sortIndex: this.sortIndex,
      limit: this.limit,
      offset: this.offset,
      lang: getDefaultReportLanguage(),
    };

    this.checksReportService.getChecksReport(params).subscribe({
      next: (res) => {
        this.rows = res.data ?? [];
        this.total = res.total ?? 0;
        this.page = res.page ?? this.currentPage;
        this.summary = res.summary ?? null;
        this.buildCharts(this.rows);
        this.loading = false;
        this.spinner.hide();
      },
      error: (err) => {
        this.loading = false;
        this.spinner.hide();
        const msg =
          err?.error?.messageAr ||
          err?.error?.messageEn ||
          this.translate.instant('checksReport.errors.loadFailed');
        Swal.fire({ icon: 'error', title: msg, timer: 3500 });
      },
    });
  }

  updateCheckStatus(row: CheckReportRow, newStatus: CheckStatus): void {
    if (row.check_status === newStatus) {
      return;
    }
    this.updatingCheckId = row.check_id;
    this.checksReportService.updateCheckStatus(row.check_id, newStatus).subscribe({
      next: (res) => {
        this.updatingCheckId = null;
        row.check_status = res.data.check_status;
        row.check_status_label = this.translate.instant(
          `checksReport.status.${res.data.check_status}`,
        );
        this.loadReport();
        const successMsg =
          this.curentLang === 'ar'
            ? res.messageAr
            : this.curentLang === 'he'
              ? res.messageHe
              : res.messageEn;
        Swal.fire({ icon: 'success', title: successMsg, timer: 2000, showConfirmButton: false });
      },
      error: (err) => {
        this.updatingCheckId = null;
        const msg =
          err?.error?.messageAr ||
          err?.error?.messageEn ||
          this.translate.instant('checksReport.errors.updateFailed');
        Swal.fire({ icon: 'error', title: msg, timer: 3500 });
      },
    });
  }

  openReceipt(row: CheckReportRow): void {
    this.router.navigate(['/dashboard/invoices-v2/view'], {
      queryParams: { invoiceId: row.receipt_id },
    });
  }

  formatNumber(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num) || !isFinite(num)) {
      return '—';
    }
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  daysUntilDueClass(days: number): string {
    if (days < 0) {
      return 'days-overdue';
    }
    if (days === 0) {
      return 'days-today';
    }
    if (days <= 7) {
      return 'days-soon';
    }
    return 'days-normal';
  }

  statusBadgeClass(status: CheckStatus): string {
    return `status-badge status-${status}`;
  }

  private loadCompanyId(): void {
    const stored = localStorage.getItem('companyDefault');
    this.companyId = stored ? Number(stored) : null;
  }

  private loadCompanies(): void {
    this.httpHelp.getAllActiveCompaniesInfoByMerchantId().subscribe({
      next: (res) => {
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        this.companyData = list.map((c: any) => ({
          company_id: c.company_id,
          label: this.getCompanyLabel(c),
        }));
        if (!this.companyId && this.companyData.length) {
          this.companyId = this.companyData[0].company_id;
        }
        this.loadReport();
      },
      error: () => {
        if (this.companyId) {
          this.loadReport();
        }
      },
    });
  }

  private getCompanyLabel(company: any): string {
    const lang = this.curentLang;
    if (lang === 'ar') {
      return company.company_name_ar || company.company_name_en || company.company_name_he || '';
    }
    if (lang === 'he') {
      return company.company_name_he || company.company_name_en || company.company_name_ar || '';
    }
    return company.company_name_en || company.company_name_ar || company.company_name_he || '';
  }

  private toApiDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private buildCharts(rows: CheckReportRow[]): void {
    const byDate = new Map<string, number>();
    rows.forEach((row) => {
      const key = row.due_date || '—';
      byDate.set(key, (byDate.get(key) || 0) + Number(row.amount || 0));
    });
    const barEntries = Array.from(byDate.entries()).sort((a, b) => {
      const da = this.parseDisplayDate(a[0]);
      const db = this.parseDisplayDate(b[0]);
      return da - db;
    });
    const maxBar = Math.max(...barEntries.map(([, amt]) => amt), 1);
    this.chartBars = barEntries.map(([label, amount]) => ({
      label,
      amount,
      pct: Math.round((amount / maxBar) * 100),
    }));

    const statusColors: Record<CheckStatus, string> = {
      pending: '#3b82f6',
      collected: '#22c55e',
      returned: '#ef4444',
      cancelled: '#94a3b8',
    };
    const byStatus = new Map<CheckStatus, { count: number; amount: number }>();
    rows.forEach((row) => {
      const cur = byStatus.get(row.check_status) || { count: 0, amount: 0 };
      cur.count += 1;
      cur.amount += Number(row.amount || 0);
      byStatus.set(row.check_status, cur);
    });
    const statusTotal = rows.length || 1;
    this.chartStatuses = Array.from(byStatus.entries()).map(([status, data]) => ({
      status,
      label: this.translate.instant(`checksReport.status.${status}`),
      count: data.count,
      amount: data.amount,
      pct: Math.round((data.count / statusTotal) * 100),
      color: statusColors[status],
    }));
  }

  private parseDisplayDate(value: string): number {
    const parts = value.split('/');
    if (parts.length === 3) {
      const d = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      let y = parseInt(parts[2], 10);
      if (y < 100) {
        y += 2000;
      }
      return new Date(y, m - 1, d).getTime();
    }
    const t = Date.parse(value);
    return isNaN(t) ? 0 : t;
  }
}
